import type { DepotRef } from '@/types';

/** Separator between depot number and name, e.g. "D001 — Köln Zentrum". */
const SEPARATOR = ' — ';

/**
 * Renders a depot as "D001 — Köln Zentrum", or just "D001" when no name is
 * known.
 *
 * The name is optional on DepotRef: events created before it was stored, and the
 * depot claim in the JWT, carry only the key. Falling back to the bare key keeps
 * those readable instead of showing a dangling separator.
 */
export function formatDepot(depot: DepotRef): string {
  return depot.name ? `${depot.key}${SEPARATOR}${depot.name}` : depot.key;
}

/** Same as formatDepot, for a list — e.g. "D001 — Köln Zentrum, D002 — Bonn". */
export function formatDepots(depots: DepotRef[]): string {
  return depots.map(formatDepot).join(', ');
}

/**
 * One depot per line, for the table's hover title.
 *
 * A native `title` renders "\n" as a line break, which a comma-separated list of
 * fourteen depots badly needs — as one line it runs off the screen edge.
 */
export function formatDepotLines(depots: DepotRef[]): string {
  return depots.map(formatDepot).join('\n');
}

/**
 * Splits a depot list into "first key + how many more" for the table column.
 *
 * Listing every key blew the column out: most events have one depot, but one has
 * fourteen, which is ~90 monospace characters in a column meant to stay narrow.
 * The first key still identifies the event at a glance; the full list is on hover.
 *
 * Returns `rest: 0` for a single depot so the caller renders the key unchanged.
 */
export function summarizeDepots(depots: DepotRef[]): { first: string; rest: number } {
  return { first: depots[0]?.key ?? '', rest: Math.max(0, depots.length - 1) };
}

/**
 * Sorts depots ascending by number: D001, D002, … D199.
 *
 * The key is `"D" + digits`, but the digit count is not fixed (three today, no
 * guarantee it stays that way) — a plain string sort would put "D10" before
 * "D2". Comparing the numeric part avoids that. Anything without a leading "D"
 * plus digits (should not occur, but a stray key must not throw) falls back to
 * a plain locale compare so it still lands somewhere sensible.
 */
export function sortDepots<T extends DepotRef>(depots: readonly T[]): T[] {
  const numberOf = (key: string): number | null => {
    const match = /^D(\d+)$/.exec(key);
    return match ? Number(match[1]) : null;
  };

  return [...depots].sort((a, b) => {
    const na = numberOf(a.key);
    const nb = numberOf(b.key);
    if (na !== null && nb !== null) return na - nb;
    if (na !== null) return -1; // numbered keys sort before non-numbered ones
    if (nb !== null) return 1;
    return a.key.localeCompare(b.key, 'de');
  });
}

